import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { BasicTable, FormattedDate, PageTopBar, PageView } from '../../components'
import { Loader } from '../../components/Loader'
import { fetchRomaneios } from '../../services/romaneiosService'

const T_PREFIX = 'romaneios'
const DEFAULT_PER_PAGE = 25
const INPUT_CLASS = 'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'
const LINK_CLASS = 'text-indigo-600 hover:text-indigo-800 underline'

const EMPTY_FILTERS = { number: '', order_number: '', carrier: '', status: '', from: '', to: '', page: 1, per_page: DEFAULT_PER_PAGE }

// Drops the empty filters, so the platform gets only what the operator set.
export const romaneiosParams = filters =>
  Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined))

const DocumentLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
    {children}
  </a>
)

// The warehouse's romaneios (the carrier's signed handover documents), open and handed over, to
// check any of them later: its PDF, its partials and its public verification page. Opened from the
// order detail with ?number= to show that one.
export const ListRomaneiosContainer = () => {
  const { i18n } = useTranslation()
  const { warehouse_id } = useParams()
  const { search } = useLocation()
  const initialNumber = useMemo(() => new URLSearchParams(search).get('number') || '', [search])

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, number: initialNumber })
  const [textInputs, setTextInputs] = useState({ number: initialNumber, order_number: '' })

  const debouncedSetText = useMemo(() => debounce((key, value) => setFilters(prev => ({ ...prev, [key]: value.trim(), page: 1 })), 300), [])
  useEffect(() => () => debouncedSetText.cancel(), [debouncedSetText])

  const handleText = key => e => {
    const value = e.target.value
    setTextInputs(prev => ({ ...prev, [key]: value }))
    debouncedSetText(key, value)
  }
  // The value is read now: by the time the updater runs, React has put the controlled select back.
  const handleSelect = key => e => {
    const value = e.target.value
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const params = useMemo(() => romaneiosParams(filters), [filters])
  const { data, isLoading, isFetching, isError } = useQuery(['romaneios', warehouse_id, params], () => fetchRomaneios(warehouse_id, params), {
    enabled: !!warehouse_id,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const columns = useMemo(
    () =>
      ['number', 'documents', 'carrier', 'status', 'pickup', 'boxes', 'driver'].map(key => ({
        Header: i18n.t(`${T_PREFIX}.headers.${key}`),
        accessor: key,
        disableSortBy: true,
      })),
    [i18n],
  )

  const tableData = useMemo(
    () =>
      (data?.romaneios || []).map(romaneio => ({
        number: (
          <div data-testid={`romaneio-${romaneio.id}`}>
            <div className="font-medium text-gray-900">{romaneio.number}</div>
            {romaneio.companion_of && (
              <div className="text-xs text-gray-500">
                {i18n.t(`${T_PREFIX}.companion_of`, { number: romaneio.companion_of.number, carrier: romaneio.companion_of.carrier_name })}
              </div>
            )}
            {romaneio.companion && <div className="text-xs text-gray-500">{i18n.t(`${T_PREFIX}.companion`, { number: romaneio.companion.number })}</div>}
          </div>
        ),
        carrier: (
          <div>
            <div>{romaneio.carrier_name}</div>
            {romaneio.schedule_name && romaneio.schedule_name !== romaneio.carrier_name && (
              <div className="text-xs text-gray-500">{romaneio.schedule_name}</div>
            )}
          </div>
        ),
        status: (
          <div className="flex flex-col gap-1 items-start">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${romaneio.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}
            >
              {i18n.t(`${T_PREFIX}.statuses.${romaneio.status}`)}
            </span>
            {romaneio.overdue && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {i18n.t(`${T_PREFIX}.overdue`)}
              </span>
            )}
          </div>
        ),
        pickup: (
          <div className="text-sm">
            {romaneio.pickup_scheduled_at && (
              <div>
                {i18n.t(`${T_PREFIX}.scheduled`)}: <FormattedDate date={romaneio.pickup_scheduled_at} shortDate={true} />
              </div>
            )}
            {romaneio.picked_up_at && (
              <div>
                {i18n.t(`${T_PREFIX}.picked_up`)}: <FormattedDate date={romaneio.picked_up_at} shortDate={true} />
              </div>
            )}
            {!romaneio.pickup_scheduled_at && !romaneio.picked_up_at && '-'}
          </div>
        ),
        boxes: (
          <div className="text-sm">
            {i18n.t(`${T_PREFIX}.boxes_count`, { count: romaneio.boxes })}
            <div className="text-xs text-gray-500">{i18n.t(`${T_PREFIX}.orders_count`, { count: romaneio.orders })}</div>
          </div>
        ),
        driver: (
          <div className="text-sm">
            {romaneio.driver ? (
              <>
                <div>{romaneio.driver.name}</div>
                <div className="text-xs text-gray-500">
                  {romaneio.driver.document_type} {romaneio.driver.document_number}
                </div>
                <div className={`text-xs ${romaneio.driver.signed ? 'text-green-700' : 'text-red-700'}`}>
                  {romaneio.driver.signed ? i18n.t(`${T_PREFIX}.signed`) : i18n.t(`${T_PREFIX}.unsigned`)}
                </div>
              </>
            ) : (
              '-'
            )}
            {(romaneio.completed_by_name || romaneio.opened_by_name) && (
              <div className="text-xs text-gray-500">
                {romaneio.completed_by_name
                  ? i18n.t(`${T_PREFIX}.handed_over_by`, { name: romaneio.completed_by_name })
                  : i18n.t(`${T_PREFIX}.opened_by`, { name: romaneio.opened_by_name })}
              </div>
            )}
          </div>
        ),
        documents: (
          <div className="flex flex-col gap-1 text-sm">
            {romaneio.document_url ? (
              <DocumentLink href={romaneio.document_url}>{i18n.t(`${T_PREFIX}.document`)}</DocumentLink>
            ) : (
              <span className="text-gray-400">
                {romaneio.status === 'open' ? i18n.t(`${T_PREFIX}.no_document_open`) : i18n.t(`${T_PREFIX}.document_pending`)}
              </span>
            )}
            {romaneio.partials.map(partial =>
              partial.document_url ? (
                <DocumentLink key={partial.sequence} href={partial.document_url}>
                  {i18n.t(`${T_PREFIX}.partial`, { sequence: partial.sequence })}
                </DocumentLink>
              ) : null,
            )}
            {romaneio.status === 'handed_over' && romaneio.verification_url && (
              <DocumentLink href={romaneio.verification_url}>{i18n.t(`${T_PREFIX}.verification`)}</DocumentLink>
            )}
          </div>
        ),
      })),
    [data, i18n],
  )

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">{i18n.t(`${T_PREFIX}.title`)}</div>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <input
          type="text"
          value={textInputs.number}
          onChange={handleText('number')}
          placeholder={i18n.t(`${T_PREFIX}.filters.number`)}
          aria-label={i18n.t(`${T_PREFIX}.filters.number`)}
          className={INPUT_CLASS}
        />
        <input
          type="text"
          value={textInputs.order_number}
          onChange={handleText('order_number')}
          placeholder={i18n.t(`${T_PREFIX}.filters.order_number`)}
          aria-label={i18n.t(`${T_PREFIX}.filters.order_number`)}
          className={INPUT_CLASS}
        />
        <select value={filters.carrier} onChange={handleSelect('carrier')} aria-label={i18n.t(`${T_PREFIX}.filters.carrier`)} className={INPUT_CLASS}>
          <option value="">{i18n.t(`${T_PREFIX}.filters.all_carriers`)}</option>
          {(data?.carriers || []).map(carrier => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </select>
        <select value={filters.status} onChange={handleSelect('status')} aria-label={i18n.t(`${T_PREFIX}.filters.status`)} className={INPUT_CLASS}>
          <option value="">{i18n.t(`${T_PREFIX}.filters.all_statuses`)}</option>
          <option value="open">{i18n.t(`${T_PREFIX}.statuses.open`)}</option>
          <option value="handed_over">{i18n.t(`${T_PREFIX}.statuses.handed_over`)}</option>
        </select>
        <label className="text-sm text-gray-600 flex flex-col">
          {i18n.t(`${T_PREFIX}.filters.from`)}
          <input type="date" value={filters.from} onChange={handleSelect('from')} className={INPUT_CLASS} />
        </label>
        <label className="text-sm text-gray-600 flex flex-col">
          {i18n.t(`${T_PREFIX}.filters.to`)}
          <input type="date" value={filters.to} onChange={handleSelect('to')} className={INPUT_CLASS} />
        </label>
      </div>

      {isError && <div className="text-red-600 text-sm mb-2">{i18n.t(`${T_PREFIX}.error`)}</div>}
      {isLoading ? (
        <Loader show={true} />
      ) : (
        <BasicTable
          columns={columns}
          data={tableData}
          showHeader={true}
          showLoader={isFetching}
          isFetching={isFetching}
          emptyTableText={i18n.t(`${T_PREFIX}.empty`)}
          showPaginator={true}
          paginationMeta={data?.meta}
          onPaginationChange={page => setFilters(prev => ({ ...prev, page }))}
          onPageSizeChange={size => setFilters(prev => ({ ...prev, per_page: Number(size), page: 1 }))}
        />
      )}
    </PageView>
  )
}
